/**
 * Générateur — Attendu 962 — Utiliser ces propriétés dans le cas de triangles
 * particuliers.
 *
 * Niveau : 5ème | Thème #10 Triangles (O21) | Domaine : Espace et géométrie
 * Source prof : cluster 5, Ex.9 (rectangle) + Créé n°36 (isocèle),
 * extraction exerciseur/triangles.
 *
 * 6 formats / 2 cartes :
 *  1. rectangle — Ex.9 : REC rectangle en E ; J milieu de [RC] → placer J
 *     (repère), aire EJC = aire REC ÷ 2 (saisie), justification (QCM).
 *  2. isocele   — Créé n°36 : MNP isocèle en M ; aire MNI = aire ÷ 2 (saisie),
 *     (MI) = médiane+hauteur+médiatrice+axe (V/F), la coïncidence ne vaut que
 *     depuis le sommet principal (QCM).
 */

import {
    randInt, randomChoice, shuffle,
    makeSaisie, makeQcm, makeVraiFaux, basePayload,
} from '../../../utils';

const ATTENDU_ID = 962;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RECTANGLE = 'Médiane dans un triangle rectangle';
const L_ISOCELE = 'Médiane dans un triangle isocèle';

const pick = (n) => shuffle('ABCDEFGHJKLMNPRSTUVWXYZ'.split('')).slice(0, n);
const frNb = (x) => {
    const s = x < 0 ? '-' : '';
    const str = Math.abs(x).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
    return s + str.replace('.', ',');
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Triangle rectangle (Ex.9) : R E C, rectangle en E
// ═══════════════════════════════════════════════════════════

const genRectanglePlacer = () => {
    // Angle droit en E ; ER vertical, EC horizontal ; côtés PAIRS → J entier.
    const ex = randInt(1, 3), ey = randInt(1, 3);
    const a = randomChoice([2, 4, 6]);   // ER
    const b = randomChoice([2, 4, 6]);   // EC
    const E = { x: ex, y: ey };
    const R = { x: ex, y: ey + a };
    const C = { x: ex + b, y: ey };
    const J = { x: ex + b / 2, y: ey + a / 2 };
    const texte = '<p>Le triangle REC est rectangle en E. Place le point <b>J</b>, '
        + 'milieu de l\'hypoténuse [RC].</p>';
    const payload = basePayload('association', texte, {
        subType: 'placer_points_2d',
        repere: { xMin: 0, yMin: 0, xMax: 10, yMax: 10, xStep: 1, yStep: 1 },
        pointsToPlace: ['J'],
        dejaPlaces: [
            { x: R.x, y: R.y, label: 'R' },
            { x: E.x, y: E.y, label: 'E' },
            { x: C.x, y: C.y, label: 'C' },
        ],
        bonne_reponse: JSON.stringify({ J: `${J.x};${J.y}` }),
    }, opts(L_RECTANGLE));
    payload._meta.generatorKey = 'rectangle_placer_points_2d';
    return payload;
};

const genRectangleTexte = () => {
    const a = randomChoice([2, 3, 4, 5, 6, 7, 8]);   // ER
    const b = randomChoice([2, 3, 4, 5, 6, 7, 8]);   // EC
    const aire = (a * b) / 2;               // aire REC
    const moit = aire / 2;                  // aire EJC
    const texte = `<p>REC est un triangle rectangle en E tel que ER = ${a} cm et EC = ${b} cm. `
        + 'J est le milieu de l\'hypoténuse [RC]. Calcule l\'aire du triangle EJC.</p>';
    const payload = makeSaisie(frNb(moit), texte, {
        ...opts(L_RECTANGLE), strict: true, suffixe: 'cm²',
    });
    payload._meta.generatorKey = 'rectangle_texte';
    return payload;
};

const genRectangleQcm = () => {
    const correct = '(EJ) est la médiane issue de E : elle partage le triangle REC en deux triangles de même aire.';
    const distract = [
        '(EJ) est une hauteur du triangle REC.',
        'Parce que ER = EC.',
        'Parce que le triangle EJC est rectangle.',
    ];
    const a = randomChoice([2, 3, 4, 5, 6, 7, 8]);
    const b = randomChoice([2, 3, 4, 5, 6, 7, 8]);
    const texte = `<p>REC est rectangle en E (ER = ${a} cm, EC = ${b} cm) et J est le milieu de [RC]. `
        + 'Pourquoi l\'aire du triangle EJC est-elle la moitié de celle de REC ?</p>';
    const payload = makeQcm([correct, ...distract], correct, texte, { ...opts(L_RECTANGLE) });
    payload._meta.generatorKey = 'rectangle_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Triangle isocèle (Créé n°36) : M N P isocèle en M
// ═══════════════════════════════════════════════════════════

const genIsoceleTexte = () => {
    const [nM, nN, nP, nI] = pick(4);
    const S = randInt(80, 200) / 10;        // aire (1 décimale)
    const moit = S / 2;
    const texte = `<p>${nM}${nN}${nP} est un triangle isocèle en ${nM}, d'aire ${frNb(S)} cm². `
        + `${nI} est le milieu de [${nN}${nP}]. Calcule l'aire du triangle ${nM}${nN}${nI}.</p>`;
    const payload = makeSaisie(frNb(moit), texte, {
        ...opts(L_ISOCELE), strict: true, suffixe: 'cm²',
    });
    payload._meta.generatorKey = 'isocele_texte';
    return payload;
};

const genIsoceleVf = () => {
    const [nM, nN, nP, nI] = pick(4);
    const lignes = [
        { t: `(${nM}${nI}) est à la fois la médiane issue de ${nM}, la hauteur relative à [${nN}${nP}], la médiatrice de [${nN}${nP}] et l'axe de symétrie du triangle.`, v: 0 },
        { t: `L'axe de symétrie du triangle isocèle passe par le sommet principal ${nM}.`, v: 0 },
        { t: `La médiane issue de ${nN} est aussi une hauteur du triangle.`, v: 1 },
        { t: 'Dans un triangle isocèle, les trois médianes sont aussi des hauteurs.', v: 1 },
    ];
    const mixed = shuffle(lignes);
    const texte = `<p>${nM}${nN}${nP} est isocèle en ${nM} et ${nI} est le milieu de [${nN}${nP}]. `
        + 'Pour chaque affirmation, vrai ou faux ?</p>';
    const payload = makeVraiFaux(mixed.map((x) => x.t), mixed.map((x) => x.v), texte, {
        ...opts(L_ISOCELE),
    });
    payload._meta.generatorKey = 'isocele_tableau_vf';
    return payload;
};

const genIsoceleQcm = () => {
    const [nM, nN, nP] = pick(3);
    const correct = `Non : cette coïncidence ne se produit que pour la droite issue du sommet principal ${nM}.`;
    const distract = [
        'Oui, cela se produit pour les trois médianes du triangle.',
        `Oui, car ${nN}${nM} = ${nP}${nM}.`,
        'On ne peut pas le savoir sans mesurer.',
    ];
    const texte = `<p>${nM}${nN}${nP} est isocèle en ${nM}. La droite issue de ${nM} vers le milieu `
        + `de [${nN}${nP}] est à la fois médiane, hauteur, médiatrice et axe de symétrie. `
        + `Cette coïncidence se produit-elle aussi pour la médiane issue de ${nN} ?</p>`;
    const payload = makeQcm([correct, ...distract], correct, texte, { ...opts(L_ISOCELE) });
    payload._meta.generatorKey = 'isocele_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    rectangle_placer_points_2d: genRectanglePlacer,
    rectangle_texte: genRectangleTexte,
    rectangle_qcm_unique: genRectangleQcm,
    isocele_texte: genIsoceleTexte,
    isocele_tableau_vf: genIsoceleVf,
    isocele_qcm_unique: genIsoceleQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie', tableau_vf: 'Vrai/Faux', placer_points_2d: 'Repère',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'rectangle', label: L_RECTANGLE, formatsList: buildFormats('rectangle') },
    { id: 'isocele', label: L_ISOCELE, formatsList: buildFormats('isocele') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genRectanglePlacer, genRectangleTexte, genRectangleQcm,
    genIsoceleTexte, genIsoceleVf, genIsoceleQcm,
};
